import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import GameCard from '../../components/GameCard/GameCard';
import AdBanner from '../../components/ADS/AdBanner';
import AdCMP from '../../components/ADS/AdCMP';
import { fetchGamezopGames } from '../../services/gameService';
import { AD_SLOTS, ADSENSE_CLIENT, slugify } from '../../utils/constants';

const fmt = (n) => {
  if (!n) return '0';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(n);
};

const ActionBtn = ({ icon, label, onClick, active }) => (
  <button onClick={onClick}
    className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer"
    style={{
      background: active ? 'rgba(79,142,247,0.15)' : 'var(--bg-hover)',
      border: `1px solid ${active ? '#4f8ef7' : 'var(--border)'}`,
      color: active ? '#4f8ef7' : 'var(--text-secondary)',
      fontFamily: 'Nunito, sans-serif',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(79,142,247,0.15)'; e.currentTarget.style.color = '#4f8ef7'; }}
    onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-secondary)'; } }}
  >
    <span>{icon}</span>
    {label && <span className="hidden sm:inline">{label}</span>}
  </button>
);

function GamesPreview() {
  const { name } = useParams();
  const [state, setState] = useState(null);
  const [gamesList, setGamesList] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [liked, setLiked] = useState(null);
  const [favorited, setFavorited] = useState(false);
  const [screenshotIdx, setScreenshotIdx] = useState(null);

  useEffect(() => {
    fetchGamezopGames()
      .then((games) => {
        setGamesList(games);
        setState(games.find((g) => slugify(g?.name?.en) === name) || null);
      }).catch(() => { });
    setPlaying(false);
    setLiked(null);
    setFavorited(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [name]);

  // Push sidebar ads after games load
  useEffect(() => {
    if (!gamesList.length) return;
    const timer = setTimeout(() => {
      try {
        const ads = document.querySelectorAll('.adsbygoogle:not([data-adsbygoogle-status])');
        ads.forEach(() => (window.adsbygoogle = window.adsbygoogle || []).push({}));
      } catch (e) { }
    }, 500);
    return () => clearTimeout(timer);
  }, [gamesList]);

  const isMobile = () => window.innerWidth < 768;
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handlePlay = () => {
    if (isMobile()) {
      window.location.href = `/games?${state?.url}&name=${state?.name?.en}`;
    } else {
      setPlaying(true);
    }
  };

  const share = () => {
    if (navigator.share) navigator.share({ title: state?.name?.en, url: window.location.href });
    else { navigator.clipboard.writeText(window.location.href); }
  };

  const relatedGames = gamesList.filter((g) => g?.name?.en && slugify(g.name.en) !== name).slice(0, 16);
  const moreGames = gamesList.filter((g) => g?.name?.en && slugify(g.name.en) !== name).slice(16, 40);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>

      {state?.url && (
        <Helmet>
          <title>{`${state.name.en} — Play Free Online | VibeOfGames`}</title>
          <meta name="description" content={`Play ${state.name.en} free online at VibeOfGames. No download needed — instant browser game!`} />
          <meta name="robots" content="index, follow" />
          <meta property="og:title" content={`${state.name.en} | VibeOfGames`} />
          <meta property="og:url" content={`https://vibeofgames.com/game/${name}`} />
          <meta property="og:image" content={state?.assets?.cover || state?.assets?.square || 'https://vibeofgames.com/logo.png'} />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <link rel="canonical" href={`https://vibeofgames.com/game/${name}`} />
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org', '@type': 'VideoGame',
            name: state.name.en,
            description: state?.description?.en || `Play ${state.name.en} free at VibeOfGames`,
            image: state?.assets?.square || 'https://vibeofgames.com/logo.png',
            url: `https://vibeofgames.com/game/${name}`,
            applicationCategory: 'Game', operatingSystem: 'Web Browser',
            author: { '@type': 'Organization', name: 'VibeOfGames' },
          })}</script>
        </Helmet>
      )}

      {/* ── Main layout ── */}
      <div className="flex flex-col xl:flex-row" style={{ minHeight: '100vh' }}>

        {/* ══ LEFT: Game Column ══ */}
        <div className="flex-1 min-w-0 p-3 sm:p-4">

          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs mb-3 flex-wrap" style={{ color: 'var(--text-muted)' }}>
            <Link to="/" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Home</Link>
            <span>›</span>
            {state?.categories?.en?.[0] && (
              <>
                <Link to={`/category/${state.categories.en[0].toLowerCase().replace(/\s+/g, '-')}`}
                  style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
                  {state.categories.en[0]}
                </Link>
                <span>›</span>
              </>
            )}
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{state?.name?.en || name?.replace(/-/g, ' ')}</span>
          </div>

          {/* ── GAME FRAME + ACTION BAR wrapper ── */}
          <div className="w-full">

            {/* GAME FRAME */}
            <div className="rounded-2xl overflow-hidden w-full"
              style={{ position: 'relative', background: '#050508', border: '1px solid var(--border)', aspectRatio: '16/9', minHeight: 200 }}>
              {playing ? (
                <>
                  <iframe src={state?.url} title={state?.name?.en}
                    className="w-full h-full border-none block" allowFullScreen />
                </>
              ) : (
                <div className="relative w-full h-full">
                  {(state?.assets?.cover || state?.assets?.square) && (
                    <img src={state?.assets?.cover || state?.assets?.square} alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ filter: 'blur(18px) brightness(0.3)', transform: 'scale(1.08)' }}
                      aria-hidden="true" />
                  )}
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 p-4">
                    <img src={state?.assets?.square} alt={state?.name?.en}
                      className="rounded-xl object-cover"
                      style={{ width: 'clamp(52px, 10vw, 72px)', height: 'clamp(52px, 10vw, 72px)', border: '2px solid rgba(255,255,255,0.18)', boxShadow: '0 8px 24px rgba(0,0,0,0.8)' }} />
                    <div className="text-center px-2">
                      <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 'clamp(13px,3vw,18px)', fontWeight: 900, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.9)', marginBottom: 2, lineHeight: 1.2 }}>
                        {state?.name?.en || name?.replace(/-/g, ' ')}
                      </h1>
                      {state?.categories?.en?.[0] && (
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
                          {state.categories.en[0]}
                        </span>
                      )}
                    </div>
                    <button onClick={handlePlay}
                      className="flex items-center gap-2 font-bold rounded-full"
                      style={{ background: 'linear-gradient(135deg, #7c3aed, #4f8ef7)', color: '#fff', border: 'none', padding: 'clamp(8px,2vw,11px) clamp(20px,5vw,36px)', fontSize: 'clamp(12px,2.5vw,14px)', fontFamily: "'Sora',sans-serif", cursor: 'pointer', boxShadow: '0 6px 20px rgba(124,58,237,0.6)', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="white">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      Play Now
                    </button>
                    <div className="hidden sm:flex items-center justify-center w-full">
                      <ins className="adsbygoogle"
                        style={{ display: 'inline-block', width: 280, height: 50 }}
                        data-ad-client={ADSENSE_CLIENT}
                        data-ad-slot={AD_SLOTS['preplay']} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ACTION BAR */}
            <div className="flex items-center gap-2 px-3"
              style={{ background: 'var(--bg-secondary)', borderRadius: '0 0 14px 14px', border: '1px solid var(--border)', borderTop: 'none', marginBottom: 16, height: 44, overflow: 'hidden' }}>
              <img src="/logo.png" alt="VibeOfGames" className="w-5 h-5 rounded flex-shrink-0" />
              <span className="text-xs font-bold flex-1 truncate min-w-0" style={{ color: 'var(--text-primary)' }}>
                {state?.name?.en}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <ActionBtn icon="👍" label={fmt(state?.gamePlays)} onClick={() => setLiked('up')} active={liked === 'up'} />
                <ActionBtn icon="👎" onClick={() => setLiked('down')} active={liked === 'down'} />
                <ActionBtn icon={favorited ? '❤️' : '🤍'} onClick={() => setFavorited(!favorited)} active={favorited} />
                <ActionBtn icon="🔗" label="Share" onClick={share} />
              </div>
            </div>

          </div>{/* end wrapper */}

          {/* ── Game Info Card ── */}
          <div className="rounded-2xl p-5 mb-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="flex gap-4 items-start mb-4">
              <img src={state?.assets?.square} alt={state?.name?.en}
                className="rounded-xl object-cover flex-shrink-0"
                style={{ width: 64, height: 64, border: '1px solid var(--border)' }} />
              <div className="flex-1 min-w-0">
                <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {state?.name?.en}
                </h2>
                <div className="flex flex-wrap gap-1.5">
                  {state?.categories?.en?.map((cat, i) => (
                    <Link key={i} to={`/category/${cat.toLowerCase().replace(/\s+/g, '-')}`}
                      style={{ fontSize: 10, fontWeight: 800, color: '#4f8ef7', background: 'rgba(79,142,247,0.12)', padding: '2px 8px', borderRadius: 5, textDecoration: 'none', textTransform: 'uppercase' }}>
                      {cat}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[
                { icon: '🎮', label: 'Plays', value: fmt(state?.gamePlays), color: '#4f8ef7' },
                { icon: '⭐', label: 'Rating', value: state?.rating || 'N/A', color: '#f59e0b' },
                { icon: '💻', label: 'Platform', value: 'Browser', color: '#22c55e' },
                { icon: '📐', label: 'Mode', value: state?.isPortrait ? 'Portrait' : 'Wide', color: '#a855f7' },
              ].map(({ icon, label, value, color }) => (
                <div key={label} className="rounded-xl text-center py-2.5 px-1"
                  style={{ background: 'var(--bg-hover)', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 16, marginBottom: 2 }}>{icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color, marginBottom: 1 }}>{value}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Description */}
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 14 }}>
              {state?.description?.en || `${state?.name?.en} is an exciting free browser game. No download needed — play instantly on any device!`}
            </p>

            {/* Tags */}
            {state?.tags?.en?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {state.tags.en.slice(0, 12).map((tag, i) => (
                  <Link key={i} to={`/tag/${tag.toLowerCase().replace(/ /g, '-')}`}
                    style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-hover)', padding: '3px 9px', borderRadius: 6, textDecoration: 'none', fontWeight: 700, border: '1px solid var(--border)' }}>
                    #{tag}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* ── Ad ── */}
          <AdBanner style={{ marginBottom: 16 }} />

          {/* ── How to Play ── */}
          <div className="rounded-2xl p-5 mb-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: 'var(--text-primary)', marginBottom: 14, borderLeft: '3px solid #4f8ef7', paddingLeft: 10 }}>
              🕹️ How to Play
            </h2>
            <div className="flex flex-col gap-3">
              {[
                { step: '1', text: 'Click "Play Now" to start the game instantly in your browser — no downloads needed.' },
                { step: '2', text: 'Use mouse, keyboard arrows, or touch controls to interact with the game.' },
                { step: '3', text: 'Follow on-screen instructions and complete objectives to progress.' },
                { step: '4', text: 'Beat your high score and challenge friends by sharing the link!' },
              ].map((s) => (
                <div key={s.step} className="flex gap-3 items-start">
                  <div className="flex items-center justify-center rounded-full flex-shrink-0 text-white text-xs font-black"
                    style={{ width: 26, height: 26, background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)', marginTop: 1 }}>
                    {s.step}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>{s.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Features ── */}
          <div className="rounded-2xl p-5 mb-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: 'var(--text-primary)', marginBottom: 14, borderLeft: '3px solid #a855f7', paddingLeft: 10 }}>
              ✨ Game Features
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: '⚡', title: 'Instant Play', desc: 'No downloads. Loads in seconds directly in your browser.', color: 'rgba(79,142,247,0.12)' },
                { icon: '📱', title: 'All Devices', desc: 'Works perfectly on mobile, tablet, and desktop.', color: 'rgba(168,85,247,0.12)' },
                { icon: '🆓', title: '100% Free', desc: 'No subscriptions, no hidden fees. Always free.', color: 'rgba(34,197,94,0.12)' },
                { icon: '🏆', title: 'High Scores', desc: 'Challenge yourself and beat your personal best.', color: 'rgba(245,158,11,0.12)' },
              ].map((f, i) => (
                <div key={i} className="flex gap-3 items-start rounded-xl p-3"
                  style={{ background: f.color, border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{f.icon}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 2 }}>{f.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Screenshots ── */}
          {state?.assets?.screens?.length > 0 && (
            <div className="rounded-2xl p-5 mb-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 16, color: 'var(--text-primary)', marginBottom: 14, borderLeft: '3px solid #22c55e', paddingLeft: 10 }}>
                📸 Screenshots
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {state.assets.screens.slice(0, 6).map((src, i) => (
                  <div key={i} className="relative cursor-pointer game-card-hover rounded-xl overflow-hidden"
                    onClick={() => setScreenshotIdx(i)}
                    style={{ aspectRatio: '16/9', border: '1px solid var(--border)' }}>
                    <img src={src} alt={`${state.name.en} screenshot ${i + 1}`}
                      className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                      style={{ background: 'rgba(0,0,0,0.5)' }}>
                      <span className="text-white text-2xl">🔍</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Screenshot Viewer Modal */}
          {screenshotIdx !== null && state?.assets?.screens && (
            <div className="fixed inset-0 z-[998] flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.92)' }}
              onClick={() => setScreenshotIdx(null)}>
              {/* Close */}
              <button className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ background: 'rgba(239,68,68,0.9)', color: '#fff', border: 'none', cursor: 'pointer' }}
                onClick={() => setScreenshotIdx(null)}>
                ✕ Close
              </button>
              {/* Prev */}
              {screenshotIdx > 0 && (
                <button className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center rounded-full"
                  style={{ width: 44, height: 44, background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 20 }}
                  onClick={e => { e.stopPropagation(); setScreenshotIdx(i => i - 1); }}>
                  ‹
                </button>
              )}
              {/* Image */}
              <img
                src={state.assets.screens[screenshotIdx]}
                alt={`Screenshot ${screenshotIdx + 1}`}
                className="max-w-[90vw] max-h-[85vh] rounded-2xl object-contain"
                style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.8)' }}
                onClick={e => e.stopPropagation()}
              />
              {/* Next */}
              {screenshotIdx < state.assets.screens.length - 1 && (
                <button className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center rounded-full"
                  style={{ width: 44, height: 44, background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 20 }}
                  onClick={e => { e.stopPropagation(); setScreenshotIdx(i => i + 1); }}>
                  ›
                </button>
              )}
              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full"
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                {screenshotIdx + 1} / {state.assets.screens.length}
              </div>
            </div>
          )}

          {/* ── More Games ── */}
          <div className="mb-4">
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              🎮 More Games
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
              {moreGames.map((_ele, i) => {
                if (!_ele?.name?.en) return null;
                return <GameCard key={i} slug={slugify(_ele.name.en)} name={_ele.name.en} banner={_ele?.assets?.square} />;
              })}
            </div>
          </div>

          {/* ── Bottom Ad ── */}
          <AdBanner style={{ marginTop: 8 }} />
        </div>

        {/* ══ RIGHT SIDEBAR ══ */}
        <div className="hidden xl:flex flex-col gap-3"
          style={{ width: 400, flexShrink: 0, borderLeft: '1px solid var(--border)', padding: '16px 12px', overflowY: 'auto', maxHeight: 'calc(100vh - 52px)', position: 'sticky', top: 52 }}>

          {/* 300x250 Ad */}
          <div className="flex flex-col items-center justify-center w-full rounded-xl" style={{ minHeight: 270, background: 'var(--bg-secondary)', border: '1px dashed var(--border)', padding: '8px' }}>
            <span className="ad-label">Advertisement</span>
            <div className="flex items-center justify-center mx-auto" style={{ width: 300, height: 250 }}>
              <ins className="adsbygoogle"
                style={{ display: 'inline-block', width: 300, height: 250 }}
                data-ad-client={ADSENSE_CLIENT}
                data-ad-slot={AD_SLOTS['300x250']} />
            </div>
          </div>

          {/* Related games */}
          <div className="text-xs font-extrabold uppercase tracking-widest px-0.5" style={{ color: 'var(--text-muted)' }}>
            Related Games
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {relatedGames.map((_ele, i) => {
              const slug = slugify(_ele.name.en);
              return (
                <Link key={i} to={`/game/${slug}`} className="game-card-hover block rounded-xl overflow-hidden"
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', textDecoration: 'none' }}>
                  <img src={_ele?.assets?.square} alt={_ele.name.en}
                    className="w-full block object-cover" style={{ aspectRatio: '1' }} />
                  <div className="text-[10px] font-bold truncate px-1.5 py-1" style={{ color: 'var(--text-primary)' }}>
                    {_ele.name.en}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* 300x600 Ad */}
          <div className="flex flex-col items-center justify-center w-full rounded-xl" style={{ minHeight: 620, background: 'var(--bg-secondary)', border: '1px dashed var(--border)', padding: '8px' }}>
            <span className="ad-label">Advertisement</span>
            <div className="flex items-center justify-center mx-auto" style={{ width: 300, height: 600 }}>
              <ins className="adsbygoogle"
                style={{ display: 'inline-block', width: 300, height: 600 }}
                data-ad-client={ADSENSE_CLIENT}
                data-ad-slot={AD_SLOTS['300x600']} />
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}

export default GamesPreview;
